/*
  TemplateManageDialog — owner/manager UI for creating, editing,
  duplicating, and soft-deleting clinic exercise-program templates.

  Layout:
   ┌───────────────────────────────────────────────────────────┐
   │  [Templates list]   │   [Editor pane for the selected]    │
   │  + New              │   name, description, tags,          │
   │  ○ Clinical Pilates │   goals[], precautions[],           │
   │  ○ GLA:D            │   sections → exercises editor       │
   └───────────────────────────────────────────────────────────┘
  Save is per-template; delete is soft (row keeps ``is_deleted``).
*/
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, Plus, Trash2, Save, Wand2, ChevronUp, ChevronDown, Copy,
  CopyPlus, ArrowUpToLine, Scissors, ArrowRightLeft, History, BookmarkPlus,
} from "lucide-react";
import { cn } from "@/lib/utils";
import TemplateVersionHistoryDialog from "./TemplateVersionHistoryDialog";
import SetBreakdownEditor from "./SetBreakdownEditor";
import { SaveAsBlockDialog } from "./ProgramBlockDialogs";

// Match the client program editor exactly — same dose vocabulary in
// the same order so a template can carry every numeric prescription
// a live program does. Rendered as a 6×2 grid.
const DOSE_FIELDS = [
  ["sets", "Sets"], ["reps", "Reps"], ["hold", "Hold"],
  ["load", "Load"], ["tempo", "Tempo"], ["rest", "Rest"],
  ["rpe", "RPE"], ["frequency", "Freq"], ["side", "Side"],
  ["apparatus", "App"], ["spring", "Spring"], ["band", "Band"],
];

// Same defaults the client program editor uses — the picker on a
// template section pre-populates the same 15 options so a section
// dropped from a template into a new program keeps its type intact.
export const DEFAULT_SECTION_TYPES = [
  { key: "warmup", label: "Warm-up" },
  { key: "mobility", label: "Mobility" },
  { key: "balance", label: "Balance" },
  { key: "strength", label: "Strength" },
  { key: "cardio", label: "Cardio" },
  { key: "pilates_reformer", label: "Pilates · Reformer" },
  { key: "pilates_trapeze", label: "Pilates · Trapeze" },
  { key: "pilates_chair", label: "Pilates · Combo Chair" },
  { key: "pilates_mat", label: "Pilates · Mat" },
  { key: "onero", label: "Onero" },
  { key: "glad", label: "GLA:D" },
  { key: "neuro", label: "Parkinson's / Neuro" },
  { key: "home", label: "Home HEP" },
  { key: "cooldown", label: "Cool-down" },
  { key: "other", label: "Other" },
];

export default function TemplateManageDialog({ open, onOpenChange, onChanged, inline = false }) {
  const [templates, setTemplates] = useState([]);
  const [selectedCode, setSelectedCode] = useState(null);
  const [editing, setEditing] = useState(null);   // full doc under edit
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [saveBlockCtx, setSaveBlockCtx] = useState(null); // { index, section }
  // Clinic-wide section-type catalogue — same source the client
  // program editor uses. When the settings screen manages this list
  // (SectionCategoriesCard), any change immediately shows up here on
  // the next dialog open. Falls back to the hardcoded defaults if
  // the API isn't reachable so first-boot clinics still get options.
  const [sectionTypes, setSectionTypes] = useState(DEFAULT_SECTION_TYPES);
  useEffect(() => {
    if (!open && !inline) return;
    let cancelled = false;
    api.get("/exercise-programs/section-categories")
      .then((r) => {
        if (cancelled) return;
        const items = (r.data?.items || []).map((i) => ({
          key: i.key, label: i.label,
        }));
        if (items.length > 0) setSectionTypes(items);
      })
      .catch(() => { /* keep the fallback */ });
    return () => { cancelled = true; };
  }, [open, inline]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/exercise-program-templates");
      setTemplates(r.data?.templates || []);
      if (!selectedCode && r.data?.templates?.length) {
        setSelectedCode(r.data.templates[0].code);
      }
    } finally { setLoading(false); }
  }, [selectedCode]);

  const loadDetail = useCallback(async (code) => {
    if (!code) { setEditing(null); return; }
    try {
      const r = await api.get(`/exercise-program-templates/${code}`);
      setEditing({ ...r.data, code });
    } catch (e) {
      toast.error("Couldn't load template", { description: e?.response?.data?.detail || e.message });
    }
  }, []);

  useEffect(() => { if (open) loadList(); }, [open, loadList]);
  useEffect(() => { loadDetail(selectedCode); }, [selectedCode, loadDetail]);

  function newTemplate() {
    const code = `clinic-${Date.now().toString(36)}`;
    setEditing({
      code,
      name: "New template",
      short_description: "",
      service_type: "exercise_physiology",
      tags: [],
      goals: [],
      precautions: [],
      sections: [],
      __new: true,
    });
    setSelectedCode(code);
  }

  async function save() {
    if (!editing) return;
    setSaving(true);
    try {
      if (editing.__new) {
        const body = { ...editing };
        delete body.__new;
        const r = await api.post("/exercise-program-templates", body);
        toast.success("Template created");
        setEditing({ ...r.data, code: r.data.code });
        setSelectedCode(r.data.code);
      } else {
        const body = {
          name: editing.name,
          short_description: editing.short_description,
          service_type: editing.service_type,
          tags: editing.tags || [],
          goals: editing.goals || [],
          precautions: editing.precautions || [],
          sections: editing.sections || [],
          pain_tracking_enabled: !!editing.pain_tracking_enabled,
          worksheet_settings: editing.worksheet_settings || {},
        };
        const r = await api.patch(`/exercise-program-templates/${editing.code}`, body);
        setEditing({ ...r.data, code: r.data.code });
        toast.success("Template saved");
      }
      await loadList();
      onChanged?.();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  async function remove() {
    if (!editing || editing.__new) return;
    if (!confirm(`Delete template "${editing.name}"? This soft-deletes it — existing programs are untouched.`)) return;
    try {
      await api.delete(`/exercise-program-templates/${editing.code}`);
      toast.success("Template deleted");
      setEditing(null); setSelectedCode(null);
      await loadList();
      onChanged?.();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function duplicate() {
    if (!editing || editing.__new) return;
    try {
      const r = await api.post(
        `/exercise-program-templates/${editing.code}/duplicate`, {},
      );
      toast.success(`Duplicated as "${r.data.name}"`);
      setSelectedCode(r.data.code);
      setEditing({ ...r.data, code: r.data.code });
      await loadList();
      onChanged?.();
    } catch (e) {
      toast.error("Duplicate failed", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }

  async function replaceWith() {
    if (!editing || editing.__new) return;
    // Pick a source template — restrict to other templates.
    const others = (templates || []).filter((t) => t.code !== editing.code);
    if (others.length === 0) {
      toast.error("Create another template first to swap with");
      return;
    }
    // Build a tiny prompt-based picker — the manage dialog is
    // already dense; a modal-within-modal would double the noise.
    // Show numbered list, take the number as input.
    const idx = window.prompt(
      `Replace "${editing.name}" with which template's content?\n\n`
        + others.map((t, i) => `${i + 1}. ${t.name}`).join("\n")
        + "\n\nEnter number (or blank to cancel):",
      "",
    );
    if (!idx) return;
    const n = parseInt(idx, 10);
    if (!n || n < 1 || n > others.length) {
      toast.error("Invalid selection");
      return;
    }
    const src = others[n - 1];
    const hideSrc = window.confirm(
      `After the swap, hide "${src.name}" from the picker?`
        + "\n\nOK = hide it, Cancel = keep it visible",
    );
    try {
      const r = await api.post(
        `/exercise-program-templates/${editing.code}/replace-with`,
        { source_code: src.code, hide_source: hideSrc, take_source_name: true },
      );
      toast.success(`"${editing.name}" now uses content from "${src.name}"`);
      setEditing({ ...r.data.target, code: r.data.target.code });
      setSelectedCode(r.data.target.code);
      await loadList();
      onChanged?.();
    } catch (e) {
      toast.error("Replace failed", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }

  // ---- helpers ----------------------------------------------------------
  const patchField = (k, v) => setEditing((d) => ({ ...d, [k]: v }));
  const patchList = (k, i, v) => setEditing((d) => {
    const arr = [...(d[k] || [])]; arr[i] = v;
    return { ...d, [k]: arr };
  });
  const addToList = (k, v = "") => setEditing((d) => ({ ...d, [k]: [...(d[k] || []), v] }));
  const removeFromList = (k, i) => setEditing((d) => {
    const arr = [...(d[k] || [])]; arr.splice(i, 1);
    return { ...d, [k]: arr };
  });

  // ---- section + exercise mutators -------------------------------------
  function addSection() {
    setEditing((d) => ({ ...d, sections: [...(d.sections || []),
      { key: `section-${Date.now().toString(36).slice(-4)}`,
        label: "New section", narrative: "", exercises: [] }] }));
  }
  function patchSection(si, changes) {
    setEditing((d) => {
      const sections = [...(d.sections || [])];
      sections[si] = { ...sections[si], ...changes };
      return { ...d, sections };
    });
  }
  function moveSection(si, delta) {
    setEditing((d) => {
      const sections = [...(d.sections || [])];
      const j = si + delta; if (j < 0 || j >= sections.length) return d;
      [sections[si], sections[j]] = [sections[j], sections[si]];
      return { ...d, sections };
    });
  }
  function removeSection(si) {
    setEditing((d) => {
      const sections = [...(d.sections || [])]; sections.splice(si, 1);
      return { ...d, sections };
    });
  }
  function addExercise(si) {
    setEditing((d) => {
      const sections = [...(d.sections || [])];
      sections[si].exercises = [...(sections[si].exercises || []),
        { name: "New exercise" }];
      return { ...d, sections };
    });
  }
  function patchExercise(si, ei, changes) {
    setEditing((d) => {
      const sections = [...(d.sections || [])];
      const exercises = [...(sections[si].exercises || [])];
      exercises[ei] = { ...exercises[ei], ...changes };
      sections[si] = { ...sections[si], exercises };
      return { ...d, sections };
    });
  }
  function removeExercise(si, ei) {
    setEditing((d) => {
      const sections = [...(d.sections || [])];
      const exercises = [...(sections[si].exercises || [])];
      exercises.splice(ei, 1);
      sections[si] = { ...sections[si], exercises };
      return { ...d, sections };
    });
  }
  function moveExercise(si, ei, delta) {
    setEditing((d) => {
      const sections = [...(d.sections || [])];
      const exercises = [...(sections[si].exercises || [])];
      const j = ei + delta;
      if (j < 0 || j >= exercises.length) return d;
      [exercises[ei], exercises[j]] = [exercises[j], exercises[ei]];
      sections[si] = { ...sections[si], exercises };
      return { ...d, sections };
    });
  }
  function moveExerciseToSection(si, ei, targetIdx) {
    setEditing((d) => {
      if (targetIdx === si) return d;
      const sections = [...(d.sections || [])];
      if (targetIdx < 0 || targetIdx >= sections.length) return d;
      const srcExs = [...(sections[si].exercises || [])];
      const [moved] = srcExs.splice(ei, 1);
      if (!moved) return d;
      sections[si] = { ...sections[si], exercises: srcExs };
      const dstExs = [...(sections[targetIdx].exercises || []), moved];
      sections[targetIdx] = { ...sections[targetIdx], exercises: dstExs };
      return { ...d, sections };
    });
  }
  function mergeSectionIntoPrevious(si) {
    if (si <= 0) return;
    setEditing((d) => {
      const sections = [...(d.sections || [])];
      if (si >= sections.length) return d;
      const prev = sections[si - 1];
      const curr = sections[si];
      sections[si - 1] = {
        ...prev,
        exercises: [...(prev.exercises || []), ...(curr.exercises || [])],
      };
      sections.splice(si, 1);
      return { ...d, sections };
    });
  }
  function splitSectionAfter(si, ei) {
    setEditing((d) => {
      const sections = [...(d.sections || [])];
      if (si < 0 || si >= sections.length) return d;
      const src = sections[si];
      const exs = [...(src.exercises || [])];
      if (ei < 0 || ei >= exs.length - 1) return d;
      const keep = exs.slice(0, ei + 1);
      const rest = exs.slice(ei + 1);
      const baseLabel = src.label || src.key || "Section";
      let suffix = 2;
      const siblingLabels = new Set(sections.map((s) => s.label));
      while (siblingLabels.has(`${baseLabel} (${suffix})`)) suffix += 1;
      const newKey = `${(src.key || "section")}-${suffix}-${Math.random().toString(36).slice(2, 6)}`;
      const newSection = {
        key: newKey,
        label: `${baseLabel} (${suffix})`,
        narrative: "",
        exercises: rest,
      };
      sections[si] = { ...src, exercises: keep };
      sections.splice(si + 1, 0, newSection);
      return { ...d, sections };
    });
  }
  function addAlternative(si, ei) {
    setEditing((d) => {
      const sections = [...(d.sections || [])];
      const exercises = [...(sections[si].exercises || [])];
      const alts = [...(exercises[ei].alternatives || [])];
      alts.push({ name: "New alternative" });
      exercises[ei] = { ...exercises[ei], alternatives: alts };
      sections[si] = { ...sections[si], exercises };
      return { ...d, sections };
    });
  }
  function patchAlternative(si, ei, ai, changes) {
    setEditing((d) => {
      const sections = [...(d.sections || [])];
      const exercises = [...(sections[si].exercises || [])];
      const alts = [...(exercises[ei].alternatives || [])];
      alts[ai] = { ...alts[ai], ...changes };
      exercises[ei] = { ...exercises[ei], alternatives: alts };
      sections[si] = { ...sections[si], exercises };
      return { ...d, sections };
    });
  }
  function removeAlternative(si, ei, ai) {
    setEditing((d) => {
      const sections = [...(d.sections || [])];
      const exercises = [...(sections[si].exercises || [])];
      const alts = [...(exercises[ei].alternatives || [])];
      alts.splice(ai, 1);
      exercises[ei] = { ...exercises[ei], alternatives: alts };
      sections[si] = { ...sections[si], exercises };
      return { ...d, sections };
    });
  }

  // The same panel is used both as a modal (Settings + Programs
  // tab launchers) AND as an inline surface on the Program Library
  // page (`/program-library?tab=templates`). When ``inline`` is set,
  // we skip the ``<Dialog>`` wrapper so the panel flows inside the
  // page grid.
  const HeaderTitle = inline ? "h2" : DialogTitle;
  const HeaderDescription = inline ? "p" : DialogDescription;
  const HeaderWrap = inline ? "div" : DialogHeader;
  const FooterWrap = inline ? "div" : DialogFooter;
  const Body = (
    <>
      <HeaderWrap className={inline ? "mb-3" : undefined}>
        <HeaderTitle className={inline
          ? "text-base font-semibold flex items-center gap-2 text-zinc-950"
          : "flex items-center gap-2"}>
          <Wand2 className="h-4 w-4 text-indigo-700" /> Manage program templates
        </HeaderTitle>
        <HeaderDescription className={inline ? "text-xs text-zinc-500 mt-1" : undefined}>
          The library is shared clinic-wide. Any change here takes effect on the next
          &quot;Start from template&quot; — existing client programs are untouched.
        </HeaderDescription>
      </HeaderWrap>

      <div className={cn(
        "flex-1 grid grid-cols-1 md:grid-cols-[240px_1fr] gap-3 overflow-hidden",
        inline && "min-h-[70vh]",
      )}>
          {/* List */}
          <div className="overflow-y-auto border-r border-zinc-100 pr-2 space-y-1">
            <Button size="sm" variant="outline" onClick={newTemplate}
                    className="w-full mb-2" data-testid="template-new">
              <Plus className="h-3.5 w-3.5 mr-1" /> New template
            </Button>
            {loading ? (
              <div className="text-sm text-zinc-500 py-6 text-center">
                <Loader2 className="h-4 w-4 inline mr-1 animate-spin" /> Loading…
              </div>
            ) : (
              templates.map((t) => (
                <div
                  key={t.code}
                  className={cn(
                    "flex items-start gap-1 rounded border transition-colors",
                    selectedCode === t.code
                      ? "border-indigo-300 bg-indigo-50/50"
                      : "border-transparent hover:bg-zinc-100",
                  )}
                >
                  <button
                    onClick={() => setSelectedCode(t.code)}
                    className="flex-1 min-w-0 text-left px-2 py-1.5"
                    data-testid={`manage-template-${t.code}`}
                  >
                    <div className="text-sm font-medium flex items-center gap-1">
                      <span className="flex-1 truncate">{t.name}</span>
                      {t.is_seeded && (
                        <Badge variant="outline" className="text-2xs">seed</Badge>
                      )}
                    </div>
                    <div className="text-2xs text-zinc-500">
                      {t.section_count} sections · {t.exercise_count} exercises
                    </div>
                  </button>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      // Confirm on seeded templates (the whole point
                      // is often to clone a seed then edit — but
                      // explicit confirm avoids accidental clones on
                      // the many-templates-listed case).
                      const suggested = window.prompt(
                        `Duplicate "${t.name}" as:`,
                        `${t.name} (copy)`,
                      );
                      if (!suggested?.trim()) return;
                      try {
                        const r = await api.post(
                          `/exercise-program-templates/${t.code}/duplicate`,
                          { name: suggested.trim() },
                        );
                        toast.success(`Duplicated as "${r.data.name}"`);
                        // Refresh the list + select the new duplicate.
                        await loadList();
                        setSelectedCode(r.data.code);
                        onChanged?.();
                      } catch (ex) {
                        toast.error("Duplicate failed", {
                          description: ex?.response?.data?.detail || ex.message,
                        });
                      }
                    }}
                    className="p-1.5 rounded hover:bg-zinc-200 text-zinc-500 hover:text-zinc-900 flex-shrink-0 self-center"
                    title={`Duplicate "${t.name}" as a new template`}
                    data-testid={`manage-template-${t.code}-duplicate`}
                  >
                    <CopyPlus className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Editor */}
          <div className="overflow-y-auto pr-1">
            {!editing ? (
              <div className="text-sm text-zinc-500 py-10 text-center">
                Pick a template on the left or create a new one.
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <label className="text-2xs uppercase text-zinc-500">Name</label>
                    <Input value={editing.name || ""}
                           onChange={(e) => patchField("name", e.target.value)}
                           data-testid="tpl-name" />
                  </div>
                  <div>
                    <label className="text-2xs uppercase text-zinc-500">Service type</label>
                    <Input value={editing.service_type || ""}
                           onChange={(e) => patchField("service_type", e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className="text-2xs uppercase text-zinc-500">Short description</label>
                  <Textarea rows={2} value={editing.short_description || ""}
                            onChange={(e) => patchField("short_description", e.target.value)} />
                </div>
                <div>
                  <label className="text-2xs uppercase text-zinc-500">Tags (comma-separated)</label>
                  <Input value={(editing.tags || []).join(", ")}
                         onChange={(e) => patchField("tags",
                           e.target.value.split(",").map((t) => t.trim()).filter(Boolean))} />
                </div>

                <StringList label="Goals" items={editing.goals || []}
                            onChange={(i, v) => patchList("goals", i, v)}
                            onAdd={() => addToList("goals", "")}
                            onRemove={(i) => removeFromList("goals", i)} />
                <StringList label="Precautions" items={editing.precautions || []}
                            onChange={(i, v) => patchList("precautions", i, v)}
                            onAdd={() => addToList("precautions", "")}
                            onRemove={(i) => removeFromList("precautions", i)}
                            variant="warning" />

                <label className="flex items-center gap-2 text-xs text-zinc-700 select-none pt-1"
                       data-testid="tpl-pain-tracking-toggle">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 accent-indigo-600"
                    checked={!!editing.pain_tracking_enabled}
                    onChange={(e) => patchField("pain_tracking_enabled", e.target.checked)}
                  />
                  <span>
                    Track pre &amp; post pain score (0-10) each session
                    <span className="ml-1 text-zinc-500 font-normal">
                      — new programs from this template will show a Pain Log tab
                    </span>
                  </span>
                </label>

                <label className="flex items-center gap-2 text-xs text-zinc-700 select-none"
                       data-testid="tpl-rpe-default-toggle">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 accent-indigo-600"
                    checked={!!(editing.worksheet_settings?.show_rpe_column)}
                    onChange={(e) => patchField("worksheet_settings", {
                      ...(editing.worksheet_settings || {}),
                      show_rpe_column: e.target.checked,
                    })}
                  />
                  <span>
                    Default RPE column on worksheet PDFs
                    <span className="ml-1 text-zinc-500 font-normal">
                      — recommended for Active Ageing / EP templates
                    </span>
                  </span>
                </label>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-2xs uppercase text-zinc-500 flex items-center gap-2">
                      Sections
                      <a
                        href="/settings?tab=clinical#section-categories"
                        target="_blank" rel="noreferrer"
                        className="normal-case text-2xs text-indigo-700 hover:underline"
                        title="Add / rename / delete the section-type options that appear in the dropdown below"
                        data-testid="tpl-manage-section-types-link"
                      >
                        Manage section types →
                      </a>
                    </div>
                    <Button size="sm" variant="ghost" onClick={addSection}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add section
                    </Button>
                  </div>
                  {(editing.sections || []).map((s, si) => (
                    <SectionEditor key={si} section={s} index={si}
                      allSections={editing.sections || []}
                      sectionTypes={sectionTypes}
                      onPatch={(changes) => patchSection(si, changes)}
                      onMove={(d) => moveSection(si, d)}
                      onRemove={() => removeSection(si)}
                      onMergeUp={() => mergeSectionIntoPrevious(si)}
                      onAddExercise={() => addExercise(si)}
                      onPatchExercise={(ei, ch) => patchExercise(si, ei, ch)}
                      onRemoveExercise={(ei) => removeExercise(si, ei)}
                      onMoveExercise={(ei, delta) => moveExercise(si, ei, delta)}
                      onMoveExerciseToSection={(ei, tgt) => moveExerciseToSection(si, ei, tgt)}
                      onSplitAfter={(ei) => splitSectionAfter(si, ei)}
                      onSaveAsBlock={() => setSaveBlockCtx({ index: si, section: s })}
                      onAddAlt={(ei) => addAlternative(si, ei)}
                      onPatchAlt={(ei, ai, ch) => patchAlternative(si, ei, ai, ch)}
                      onRemoveAlt={(ei, ai) => removeAlternative(si, ei, ai)} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <FooterWrap className="border-t pt-3">
          <div className="flex items-center gap-2 w-full">
            {editing && !editing.__new && (
              <>
                <Button variant="outline" onClick={duplicate}
                        data-testid="tpl-duplicate">
                  <CopyPlus className="h-3.5 w-3.5 mr-1" /> Duplicate as new
                </Button>
                <Button variant="outline" onClick={replaceWith}
                        data-testid="tpl-replace-with"
                        title="Overwrite this template's content with another template's — useful for swapping a seeded template for a more detailed clinic-authored one">
                  <ArrowRightLeft className="h-3.5 w-3.5 mr-1" /> Replace with…
                </Button>
                <Button variant="outline" onClick={() => setHistoryOpen(true)}
                        data-testid="tpl-history"
                        title="Preview and roll back to a previous version of this template — snapshots are captured on Replace-with or Restore">
                  <History className="h-3.5 w-3.5 mr-1" /> History
                </Button>
                <Button variant="ghost" onClick={remove}
                        className="text-red-600" data-testid="tpl-delete">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                </Button>
              </>
            )}
            <div className="flex-1" />
            {!inline && (
              <Button variant="ghost" onClick={() => onOpenChange?.(false)}>Close</Button>
            )}
            <Button onClick={save} disabled={!editing || saving} data-testid="tpl-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
              Save changes
            </Button>
          </div>
        </FooterWrap>
    </>
  );

  const history = (
    <>
      {editing && !editing.__new && (
        <TemplateVersionHistoryDialog
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          templateCode={editing.code}
          templateName={editing.name}
          onRestored={async () => {
            await loadList();
            await loadDetail(editing.code);
            onChanged?.();
          }}
        />
      )}
      <SaveAsBlockDialog
        open={!!saveBlockCtx}
        onOpenChange={(v) => { if (!v) setSaveBlockCtx(null); }}
        section={saveBlockCtx?.section}
        sectionContent={saveBlockCtx?.section}
        sourceKind="template"
      />
    </>
  );

  if (inline) {
    return (
      <div className="flex flex-col" data-testid="template-manage-panel-inline">
        {Body}
        {history}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[96vw] max-h-[92vh] overflow-hidden flex flex-col"
                     data-testid="template-manage-dialog">
        {Body}
      </DialogContent>
      {history}
    </Dialog>
  );
}


function StringList({ label, items, onChange, onAdd, onRemove, variant }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-2xs uppercase text-zinc-500">{label}</div>
        <Button size="sm" variant="ghost" onClick={onAdd}>
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="space-y-1">
        {items.map((v, i) => (
          <div key={i} className={cn("flex items-center gap-1 rounded border px-1",
                                       variant === "warning"
                                         ? "border-amber-200 bg-amber-50/40" : "border-zinc-200")}>
            <Input value={v} onChange={(e) => onChange(i, e.target.value)}
                   className="h-7 text-sm border-0 focus-visible:ring-0" />
            <Button size="sm" variant="ghost" onClick={() => onRemove(i)}>
              <Trash2 className="h-3 w-3 text-red-500" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}


export function SectionEditor({ section, index, allSections = [],
                         sectionTypes = DEFAULT_SECTION_TYPES,
                         singleSection = false,
                         onPatch, onMove, onRemove, onMergeUp,
                         onAddExercise, onPatchExercise, onRemoveExercise,
                         onMoveExercise, onMoveExerciseToSection, onSplitAfter,
                         onSaveAsBlock,
                         onAddAlt, onPatchAlt, onRemoveAlt }) {
  const [expanded, setExpanded] = useState(true);
  const exCount = (section.exercises || []).length;
  // If the section already has a `key` that isn't in the clinic's
  // catalogue (e.g. a template imported before a category was
  // renamed), inject a synthetic entry so the current value is still
  // selectable — mirrors the client editor's fallback pattern.
  const inCatalog = sectionTypes.some((t) => t.key === section.key);
  const options = (section.key && !inCatalog)
    ? sectionTypes
    : sectionTypes;
  const augmented = (section.key && !inCatalog)
    ? [...options, { key: section.key,
                      label: section.label || section.key || "(unknown)" }]
    : options;
  return (
    <div className="border border-zinc-200 rounded p-2 mb-2 space-y-2">
      <div className="flex items-center gap-1">
        <Button size="sm" variant="ghost" onClick={() => setExpanded((v) => !v)}>
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </Button>
        <select
          value={section.key || ""}
          onChange={(e) => {
            const key = e.target.value;
            const preset = augmented.find((t) => t.key === key);
            onPatch({
              key,
              // Auto-fill the label when the operator picks a section
              // type on an unnamed section — matches client editor UX.
              label: (section.label && section.label.trim())
                ? section.label
                : (preset?.label || ""),
            });
          }}
          className="h-7 text-xs border border-zinc-300 rounded px-1 bg-white max-w-[10rem]"
          data-testid={`tpl-section-type-${index}`}
          title="Section type — carries into the client program when used. Manage the list under Settings → Clinical → Section categories."
        >
          <option value="">Section type…</option>
          {augmented.map((t) => (
            <option key={t.key} value={t.key}>{t.label}</option>
          ))}
        </select>
        <Input value={section.label || ""}
               onChange={(e) => onPatch({ label: e.target.value })}
               className="h-7 text-sm font-medium flex-1" placeholder="Section label" />
        {!singleSection && (<>
        <Button size="sm" variant="ghost" onClick={() => onMove(-1)} title="Move up">
          <ChevronUp className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onMove(1)} title="Move down">
          <ChevronDown className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="sm" variant="ghost"
          onClick={() => onSaveAsBlock?.()}
          disabled={!(section.exercises || []).length}
          title="Save this section to the reusable Block Library (Draft v1)"
          data-testid={`tpl-save-block-${index}`}
        >
          <BookmarkPlus className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="sm" variant="ghost"
          onClick={() => {
            if (window.confirm(`Merge "${section.label || section.key || "section"}" into the previous section? All exercises will move up.`)) {
              onMergeUp?.();
            }
          }}
          disabled={index === 0}
          title="Merge into previous section"
          data-testid={`tpl-merge-section-${index}`}
        >
          <ArrowUpToLine className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="ghost" onClick={onRemove}>
          <Trash2 className="h-3.5 w-3.5 text-red-500" />
        </Button>
        </>)}
      </div>
      {expanded && (
        <>
          <Textarea rows={1} value={section.narrative || ""}
                    onChange={(e) => onPatch({ narrative: e.target.value })}
                    placeholder="Section narrative (optional)"
                    className="text-xs text-zinc-700" />
          <div className="space-y-2">
            {(section.exercises || []).map((ex, ei) => (
              <ExerciseEditor key={ei} exercise={ex}
                sectionIndex={index}
                allSections={allSections}
                onPatch={(ch) => onPatchExercise(ei, ch)}
                onRemove={() => onRemoveExercise(ei)}
                onMoveUp={() => onMoveExercise?.(ei, -1)}
                onMoveDown={() => onMoveExercise?.(ei, 1)}
                canMoveUp={ei > 0}
                canMoveDown={ei < exCount - 1}
                onMoveToSection={(tgt) => onMoveExerciseToSection?.(ei, tgt)}
                onSplitAfter={() => onSplitAfter?.(ei)}
                canSplitAfter={!singleSection && ei < exCount - 1}
                onAddAlt={() => onAddAlt(ei)}
                onPatchAlt={(ai, ch) => onPatchAlt(ei, ai, ch)}
                onRemoveAlt={(ai) => onRemoveAlt(ei, ai)} />
            ))}
            <Button size="sm" variant="outline" onClick={onAddExercise}
                    data-testid={`tpl-add-exercise-${index}`}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Add exercise
            </Button>
          </div>
        </>
      )}
    </div>
  );
}


function ExerciseEditor({ exercise, sectionIndex = 0, allSections = [],
                          onPatch, onRemove,
                          onMoveUp, onMoveDown, canMoveUp, canMoveDown,
                          onMoveToSection, onSplitAfter, canSplitAfter,
                          onAddAlt, onPatchAlt, onRemoveAlt }) {
  const [altsOpen, setAltsOpen] = useState((exercise.alternatives || []).length > 0);
  const otherSections = (allSections || [])
    .map((s, idx) => ({ idx, label: s.label || s.key || `Section ${idx + 1}` }))
    .filter((s) => s.idx !== sectionIndex);
  return (
    <div className="border border-zinc-100 rounded p-2 bg-zinc-50/40 space-y-1">
      <div className="flex items-center gap-1">
        <div className="flex flex-col -my-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={!canMoveUp}
            className="h-3.5 w-5 grid place-items-center text-zinc-400 hover:text-zinc-900 disabled:opacity-30 disabled:hover:text-zinc-400"
            title="Move up"
            data-testid="tpl-ex-move-up"
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={!canMoveDown}
            className="h-3.5 w-5 grid place-items-center text-zinc-400 hover:text-zinc-900 disabled:opacity-30 disabled:hover:text-zinc-400"
            title="Move down"
            data-testid="tpl-ex-move-down"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </div>
        <Input value={exercise.name || ""}
               onChange={(e) => onPatch({ name: e.target.value })}
               placeholder="Exercise name"
               className="h-7 text-sm font-medium flex-1" />
        {otherSections.length > 0 && (
          <select
            value=""
            onChange={(e) => {
              const idx = parseInt(e.target.value, 10);
              if (!Number.isNaN(idx)) onMoveToSection?.(idx);
              e.target.value = "";
            }}
            className="h-7 text-2xs border border-zinc-200 rounded bg-white text-zinc-700 px-1"
            title="Move to another section"
            data-testid="tpl-ex-move-to"
          >
            <option value="">Move to…</option>
            {otherSections.map((o) => (
              <option key={o.idx} value={o.idx}>{o.label}</option>
            ))}
          </select>
        )}
        {canSplitAfter && (
          <button
            type="button"
            onClick={onSplitAfter}
            className="h-7 w-6 grid place-items-center text-zinc-400 hover:text-zinc-900"
            title="Split section after this exercise"
            data-testid="tpl-ex-split-after"
          >
            <Scissors className="h-3.5 w-3.5" />
          </button>
        )}
        <Button size="sm" variant="ghost" onClick={onRemove}>
          <Trash2 className="h-3.5 w-3.5 text-red-500" />
        </Button>
      </div>
      <div className="grid grid-cols-6 gap-1">
        {DOSE_FIELDS.map(([k, lbl]) => (
          <Input key={k} value={exercise[k] || ""}
                 onChange={(e) => onPatch({ [k]: e.target.value })}
                 placeholder={lbl} className="h-7 text-xs"
                 data-testid={`tpl-ex-${k}`} />
        ))}
      </div>
      <Textarea rows={1} value={exercise.instructions || ""}
                onChange={(e) => onPatch({ instructions: e.target.value })}
                placeholder="Instructions / cues (visible to client)"
                className="text-xs" />
      <Textarea rows={1} value={exercise.precautions || ""}
                onChange={(e) => onPatch({ precautions: e.target.value })}
                placeholder="Precautions (surfaces on the client-facing PDF)"
                className="text-xs bg-amber-50/40" />
      <SetBreakdownEditor
        ex={exercise}
        locked={false}
        onChange={(patch) => onPatch(patch)}
      />
      <div>
        <button onClick={() => setAltsOpen((v) => !v)}
                className="text-2xs uppercase text-indigo-700 hover:underline mt-1">
          <Copy className="h-3 w-3 inline mr-1" />
          {altsOpen ? "Hide" : "Show"} alternatives ({(exercise.alternatives || []).length})
        </button>
        {altsOpen && (
          <div className="mt-1 space-y-1">
            {(exercise.alternatives || []).map((alt, ai) => (
              <div key={ai} className="border border-zinc-200 rounded p-1 bg-white space-y-1">
                <div className="flex items-center gap-1">
                  <Input value={alt.name || ""}
                         onChange={(e) => onPatchAlt(ai, { name: e.target.value })}
                         className="h-7 text-sm flex-1" placeholder="Alternative name" />
                  <Button size="sm" variant="ghost" onClick={() => onRemoveAlt(ai)}>
                    <Trash2 className="h-3 w-3 text-red-500" />
                  </Button>
                </div>
                <div className="grid grid-cols-6 gap-1">
                  {DOSE_FIELDS.map(([k, lbl]) => (
                    <Input key={k} value={alt[k] || ""}
                           onChange={(e) => onPatchAlt(ai, { [k]: e.target.value })}
                           placeholder={lbl} className="h-7 text-xs" />
                  ))}
                </div>
              </div>
            ))}
            <Button size="sm" variant="ghost" onClick={onAddAlt}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Add alternative
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
